/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { ComplianceReviewStatus, PaymentMethodFields } from "~/features/myAccount/types";
import { ApiMessageConfig, CollapsiblePaperTagConfig } from "~/types";

// OFAC sanctioned countries
export const OFAC = ["CU", "IR", "KP", "RU", "SY"];
export const LEGAL_AGE = 18;

export enum ComplianceStatus {
  NOT_FOUND = -1,
  PENDING = 0,
  REJECTED = 1,
  APPROVED = 2,
}

export enum ResellerType {
  INDIVIDUAL = "individual",
  BUSINESS = "business",
}

// :')
export enum TaxpayerRegionAnswer {
  YES = "Y",
  NO = "N",
}

export enum TaxpayerRegion {
  US = "us",
  EU = "eu",
  OTHER = "other",
}

export const PAYMETHOD_CHECK = 5;
export const PAYMETHOD_WIRE = 6;
export const PAYMETHOD_PAXUM = 9;
export const PAYMETHOD_COSMO = 10;
export const PAYMETHOD_ACH = 11;
export const PAYMETHOD_MASSPAY = 12;
export const PAYMETHOD_ROCKETFUEL = 14;

export enum PayMethod {
  CHECK = PAYMETHOD_CHECK,
  WIRE = PAYMETHOD_WIRE,
  PAXUM = PAYMETHOD_PAXUM,
  COSMO = PAYMETHOD_COSMO,
  ACH = PAYMETHOD_ACH,
  MASSPAY = PAYMETHOD_MASSPAY,
  ROCKETFUEL = PAYMETHOD_ROCKETFUEL,
}

// dollar
export const PayMethodMinimumAmount: Record<number, number> = {
  [PayMethod.CHECK]: 20,
  [PayMethod.WIRE]: 240,
  [PayMethod.PAXUM]: 100,
  [PayMethod.COSMO]: 100,
  [PayMethod.ACH]: 100,
  [PayMethod.MASSPAY]: 100,
  [PayMethod.ROCKETFUEL]: 100,
};

export enum AchAccountType {
  CHECKING = "c",
  SAVINGS = "s",
}

export enum DocumentType {
  PROOF_OF_ID = "Proof of ID",
  PROOF_OF_ADDRESS = "Proof of Address",
  IRS_FORM = "IRS Form W8-BEN / W8-BEN-E / W9",
  ARTICLES_OF_INCORPORATION = "Articles of Incorporation",
  ARTICLES_OF_ASSOCIATION = "Articles of Association",
  OPERATING_AGREEMENT = "Operating Agreement",
  CERTIFICATE_OF_FORMATION = "Certificate of Formation",
  BY_LAWS = "By-Laws",
  OTHER = "Other",
  PROOF_OF_ID_OLD = "Personal ID",
  IRS_FORM_OLD = "IRS Form W8-BEN / W9",
}

export const DocumentTypeMap = (() => {
  const inverse: Partial<Record<DocumentType, string>> = {};

  Object.keys(DocumentType).forEach((key) => {
    inverse[DocumentType[key as keyof typeof DocumentType]] = key;
  });

  return inverse as Record<DocumentType, string>;
})();

export const ENABLED_DOCUMENT_TYPES_INDIVIDUAL: (keyof typeof DocumentType)[] = [
  "PROOF_OF_ID",
  "PROOF_OF_ADDRESS",
  "IRS_FORM",
];

export const ENABLED_DOCUMENT_TYPES_BUSINESS: (keyof typeof DocumentType)[] = [
  "PROOF_OF_ID",
  "PROOF_OF_ADDRESS",
  "IRS_FORM",
  "ARTICLES_OF_INCORPORATION",
  "ARTICLES_OF_ASSOCIATION",
  "OPERATING_AGREEMENT",
  "CERTIFICATE_OF_FORMATION",
  "BY_LAWS",
  "OTHER",
];

export const DOC_LIMIT_PER_TYPE = 3;

export const MESSAGE_DURATION = 4000;

export const euTaxIdConfig: { [key: string]: { [key: string]: string } } = {
  AT: { [ResellerType.INDIVIDUAL]: "931736581", [ResellerType.BUSINESS]: "ATU01234567" },
  BE: { [ResellerType.INDIVIDUAL]: "00012511119", [ResellerType.BUSINESS]: "BE0123456789" },
  BG: { [ResellerType.INDIVIDUAL]: "7501010010", [ResellerType.BUSINESS]: "BG0123456789" },
  CY: { [ResellerType.INDIVIDUAL]: "00123123T", [ResellerType.BUSINESS]: "CY01234567L" },
  CZ: { [ResellerType.INDIVIDUAL]: "120101789", [ResellerType.BUSINESS]: "CZ0123456789" },
  DE: { [ResellerType.INDIVIDUAL]: "26954371827", [ResellerType.BUSINESS]: "DE012345678" },
  DK: { [ResellerType.INDIVIDUAL]: "0101111113", [ResellerType.BUSINESS]: "DK01234567" },
  EE: { [ResellerType.INDIVIDUAL]: "37102250382", [ResellerType.BUSINESS]: "EE012345678" },
  ES: { [ResellerType.INDIVIDUAL]: "X1234567L", [ResellerType.BUSINESS]: "ESA0123456A" },
  FI: { [ResellerType.INDIVIDUAL]: "131052-308T", [ResellerType.BUSINESS]: "FI01234567" },
  FR: { [ResellerType.INDIVIDUAL]: "3023217600053", [ResellerType.BUSINESS]: "FRAA012345678" },
  GR: { [ResellerType.INDIVIDUAL]: "123456789", [ResellerType.BUSINESS]: "EL012345678" },
  HR: { [ResellerType.INDIVIDUAL]: "94577403194", [ResellerType.BUSINESS]: "HR01234567890" },
  HU: { [ResellerType.INDIVIDUAL]: "8071592153", [ResellerType.BUSINESS]: "HU01234567" },
  IE: { [ResellerType.INDIVIDUAL]: "1234567T", [ResellerType.BUSINESS]: "IE0S01234L" },
  IT: { [ResellerType.INDIVIDUAL]: "33309240064", [ResellerType.BUSINESS]: "IT01234567890" },
  LT: { [ResellerType.INDIVIDUAL]: "33309240064", [ResellerType.BUSINESS]: "LT012345678901" },
  LU: { [ResellerType.INDIVIDUAL]: "1893120105732", [ResellerType.BUSINESS]: "LU01234567" },
  LV: { [ResellerType.INDIVIDUAL]: "32579461005", [ResellerType.BUSINESS]: "LV01234567890" },
  MT: { [ResellerType.INDIVIDUAL]: "1234567A", [ResellerType.BUSINESS]: "MT01234567" },
  NL: { [ResellerType.INDIVIDUAL]: "174559434", [ResellerType.BUSINESS]: "NL012345678B01" },
  PL: { [ResellerType.INDIVIDUAL]: "299999998", [ResellerType.BUSINESS]: "PL0123456789" },
  PT: { [ResellerType.INDIVIDUAL]: "299999998", [ResellerType.BUSINESS]: "PT012345678" },
  RO: { [ResellerType.INDIVIDUAL]: "8001011234567", [ResellerType.BUSINESS]: "RO0123456789" },
  SE: { [ResellerType.INDIVIDUAL]: "196408833231", [ResellerType.BUSINESS]: "SE012345678901" },
  SI: { [ResellerType.INDIVIDUAL]: "15012557", [ResellerType.BUSINESS]: "SI01234567" },
  SK: { [ResellerType.INDIVIDUAL]: "7711167420", [ResellerType.BUSINESS]: "SK0123456789" },
};

export const PAYMENT_METHOD_FIELDS: PaymentMethodFields = {
  [PayMethod.CHECK]: [],
  [PayMethod.WIRE]: [
    "pri_country",
    "pri_bank_name",
    "pri_accountholder",
    "pri_city",
    "pri_address1",
    "pri_state",
    "pri_zip",
    "pri_account_number",
    "pri_routing_code",
  ],
  [PayMethod.PAXUM]: ["business_registration_number", "paxum_email"],
  [PayMethod.COSMO]: ["cosmo_san"],
  [PayMethod.ACH]: ["ach_accountholder", "ach_bank_name", "ach_account_number", "ach_routing_code", "ach_account_type"],
  [PayMethod.MASSPAY]: ["masspay_email"],
  [PayMethod.ROCKETFUEL]: ["rocketfuel_email", "rocketfuel_first_name", "rocketfuel_last_name"],
};

export const myAccountApiMessages: ApiMessageConfig = {
  "Passwords are same": { key: "api.same_password", namespace: "forgotPassword" }, // entered password is the current password
  ACCOUNT_CHANGES_REQUIRE_VALID_PASSWORD: {
    key: "my_details.response.ACCOUNT_CHANGES_REQUIRE_VALID_PASSWORD",
    namespace: "myAccount",
  },
};

export enum ComplianceReviewStatusName {
  PENDING = "Pending",
  REJECTED = "Rejected",
  APPROVED = "Approved",
  UNKNOWN = "Unknown",
}

export enum CompliancePayeeType {
  "INDIVIDUAL" = "individual",
  "BUSINESS" = "business",
}

export const CompliancePayeeTypePayload = { [CompliancePayeeType.INDIVIDUAL]: 0, [CompliancePayeeType.BUSINESS]: 1 };

export enum ComplianceTaxpayerType {
  "US" = "us",
  "EU" = "eu",
  "OTHER" = "other",
}

export const ComplianceTaxpayerTypePayload = {
  [ComplianceTaxpayerType.US]: 0,
  [ComplianceTaxpayerType.EU]: 1,
  [ComplianceTaxpayerType.OTHER]: 2,
};

export enum ComplianceDocumentStatus {
  PENDING = "pending",
  SUBMITTED = "submitted",
}

export const ComplianceDocumentStatusColorMap: Record<ComplianceDocumentStatus, string> = {
  [ComplianceDocumentStatus.PENDING]: "warning",
  [ComplianceDocumentStatus.SUBMITTED]: "baseInvariant",
};

export const ComplianceReviewStatusTagMap: Record<ComplianceReviewStatus, CollapsiblePaperTagConfig> = {
  [ComplianceReviewStatus.UNKNOWN]: { color: "baseInvariant", label: "unknown" },
  [ComplianceReviewStatus.PENDING]: { color: "warning", label: "documents.review_status.pending.status_1" },
  [ComplianceReviewStatus.DRAFT]: { color: "warning", label: "documents.review_status.pending.status_2" },
  [ComplianceReviewStatus.REJECTED]: { color: "error", label: "documents.review_status.rejected.status" },
  [ComplianceReviewStatus.APPROVED]: { color: "success", label: "documents.review_status.approved.status" },
};
